import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import BrandLogo from "../components/BrandLogo";
import { STATE_CONFIG } from "../components/StateCourtPanel";
import { ArrowSquareOut, MapPin, ShieldCheck, CheckCircle, Globe, TrendDown, ChartLineUp } from "@phosphor-icons/react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Coverage() {
    const entries = Object.entries(STATE_CONFIG)
        .map(([code, cfg]) => ({ code, ...cfg }))
        .sort((a, b) => a.name.localeCompare(b.name));

    const [stats, setStats] = useState({});
    useEffect(() => {
        axios.get(`${API}/intelligence/state-benchmarks`)
            .then((r) => setStats(r.data.by_state || {}))
            .catch(() => setStats({}));
    }, []);

    // Page meta + JSON-LD structured data for SEO (Google rich snippets).
    useEffect(() => {
        const prevTitle = document.title;
        document.title = `Court coverage — ${entries.length} states + Federal RECAP | LienLift Direct`;
        const desc = `LienLift Direct integrates with every major free public-records court portal in ${entries.length} states plus federal PACER/RECAP. Look up judgments, abstracts, and liens in seconds.`;
        let metaDesc = document.querySelector('meta[name="description"]');
        if (!metaDesc) {
            metaDesc = document.createElement("meta");
            metaDesc.setAttribute("name", "description");
            document.head.appendChild(metaDesc);
        }
        const prevDesc = metaDesc.getAttribute("content");
        metaDesc.setAttribute("content", desc);

        // Build structured-data graph
        const origin = window.location.origin;
        const itemListElements = entries.map((e, i) => {
            const s = stats[e.code];
            const item = {
                "@type": "Service",
                "position": i + 1,
                "name": `${e.name} court records lookup`,
                "areaServed": { "@type": "State", "name": e.name },
                "provider": { "@type": "Organization", "name": "LienLift Direct" },
                "description": `${e.label} — ${e.badge}`,
                "url": `${origin}/coverage`,
            };
            if (s && s.settled_count >= 3) {
                item.aggregateRating = {
                    "@type": "AggregateRating",
                    "ratingValue": (100 - s.avg_settlement_pct).toFixed(1),
                    "bestRating": "100",
                    "ratingCount": s.settled_count,
                    "reviewAspect": "average debt reduction percentage on settled cases",
                };
            }
            return { "@type": "ListItem", position: i + 1, item };
        });
        const ld = {
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "Organization",
                    "@id": `${origin}/#org`,
                    "name": "LienLift Direct",
                    "url": origin,
                    "logo": `${origin}/logo.png`,
                    "description": "Judgment information & document service. Not a law firm.",
                },
                {
                    "@type": "WebPage",
                    "@id": `${origin}/coverage`,
                    "url": `${origin}/coverage`,
                    "name": `Court coverage — ${entries.length} states + Federal RECAP`,
                    "description": desc,
                    "isPartOf": { "@id": `${origin}/#org` },
                },
                {
                    "@type": "ItemList",
                    "name": `States covered by LienLift Direct`,
                    "numberOfItems": entries.length,
                    "itemListElement": itemListElements,
                },
            ],
        };

        const scriptId = "ll-coverage-ld";
        let script = document.getElementById(scriptId);
        if (!script) {
            script = document.createElement("script");
            script.id = scriptId;
            script.type = "application/ld+json";
            document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(ld);

        // Canonical link
        let canonical = document.querySelector('link[rel="canonical"]');
        const prevCanonical = canonical?.getAttribute("href");
        if (!canonical) {
            canonical = document.createElement("link");
            canonical.setAttribute("rel", "canonical");
            document.head.appendChild(canonical);
        }
        canonical.setAttribute("href", `${origin}/coverage`);

        return () => {
            document.title = prevTitle;
            if (prevDesc != null) metaDesc.setAttribute("content", prevDesc);
            const s = document.getElementById(scriptId);
            if (s) s.remove();
            if (prevCanonical) canonical.setAttribute("href", prevCanonical);
        };
    }, [entries, stats]);

    return (
        <div className="min-h-screen bg-white" data-testid="coverage-page">
            <header className="border-b border-slate-200 bg-white sticky top-0 z-30">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/"><BrandLogo height={36} /></Link>
                    <div className="flex items-center gap-3">
                        <Link to="/login"><Button variant="ghost" size="sm">Sign in</Button></Link>
                        <Link to="/register"><Button size="sm" className="bg-[#0F1E3D] hover:bg-[#1A2C57]">Get started</Button></Link>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-16 space-y-14">
                <section>
                    <p className="overline">Court coverage</p>
                    <h1 className="font-serif text-4xl sm:text-6xl mt-3 leading-tight">
                        Live in <span className="text-[#2563EB]">{entries.length}</span> states · Federal RECAP nationwide
                    </h1>
                    <p className="text-slate-600 mt-5 max-w-2xl text-lg">
                        LienLift Direct integrates with every major free public-records court portal so you can look up
                        judgments, abstracts, and liens in seconds — then capture the data straight into a case file
                        with AI-drafted dispute and settlement letters ready to go.
                    </p>
                    <div className="mt-6 flex items-center gap-2 text-sm text-slate-500">
                        <ShieldCheck size={16} weight="duotone" className="text-emerald-600" />
                        <span>Information &amp; document service — not a law firm.</span>
                    </div>
                </section>

                <section>
                    <div className="flex items-center gap-2 mb-5">
                        <Globe size={20} weight="duotone" className="text-[#0F1E3D]" />
                        <h2 className="font-serif text-2xl text-[#0F1E3D]">Federal</h2>
                    </div>
                    <Card className="p-6 flex items-start justify-between gap-4 flex-wrap" data-testid="coverage-federal">
                        <div>
                            <h3 className="font-medium text-slate-900">PACER / RECAP via CourtListener</h3>
                            <p className="text-sm text-slate-600 mt-1 max-w-xl">
                                Every federal district, bankruptcy, and appellate court — auto-fetch of free RECAP PDFs
                                with Gemini OCR so the documents are searchable inside each case.
                            </p>
                        </div>
                        <a href="https://www.courtlistener.com/" target="_blank" rel="noreferrer">
                            <Button variant="outline" size="sm" className="h-9"><ArrowSquareOut size={13} className="mr-1.5" />CourtListener</Button>
                        </a>
                    </Card>
                </section>

                <section>
                    <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                        <div className="flex items-center gap-2">
                            <MapPin size={20} weight="duotone" className="text-[#0F1E3D]" />
                            <h2 className="font-serif text-2xl text-[#0F1E3D]">State courts ({entries.length})</h2>
                        </div>
                        <span className="text-xs text-slate-500">Direct deep-links + structured-capture handoff</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="coverage-state-grid">
                        {entries.map((e) => {
                            const s = stats[e.code];
                            const slug = e.name.toLowerCase().replace(/\s+/g, "-");
                            return (
                                <Link key={e.code} to={`/coverage/${slug}`} className="block" data-testid={`coverage-state-link-${e.code}`}>
                                <Card className="p-5 flex flex-col gap-2.5 h-full hover:border-[#0F1E3D] transition-colors" data-testid={`coverage-state-${e.code}`}>
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex items-center gap-2">
                                            <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-[#0F1E3D] text-white text-xs font-mono font-semibold">
                                                {e.code}
                                            </span>
                                            <div>
                                                <h3 className="font-medium text-slate-900 leading-tight">{e.name}</h3>
                                                <p className="text-[11px] text-slate-500 mt-0.5">{e.portalName}</p>
                                            </div>
                                        </div>
                                        <CheckCircle size={16} weight="fill" className="text-emerald-600 shrink-0 mt-1" />
                                    </div>
                                    <p className="text-xs text-slate-500">{e.badge}</p>
                                    {s && (
                                        <div className="mt-2 pt-3 border-t border-slate-100 flex items-center gap-3 text-[11px]" data-testid={`coverage-stat-${e.code}`}>
                                            <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                                                <ChartLineUp size={11} weight="bold" /> {s.avg_settlement_pct}% avg
                                            </span>
                                            <span className="text-slate-500">{s.settled_count} settled</span>
                                            <span className="inline-flex items-center gap-1 text-slate-600 ml-auto">
                                                <TrendDown size={11} /> ${(s.total_savings / 1000).toFixed(1)}k saved
                                            </span>
                                        </div>
                                    )}
                                </Card>
                                </Link>
                            );
                        })}
                    </div>
                </section>

                <section className="text-center bg-slate-50 rounded-lg p-10 border border-slate-200">
                    <h2 className="font-serif text-3xl text-[#0F1E3D]">Don&apos;t see your state yet?</h2>
                    <p className="text-slate-600 mt-3 max-w-xl mx-auto">
                        We&apos;re adding new portals every release. Federal RECAP is already live nationwide, and our manual
                        capture form lets you record any judgment from any court — your case file still gets the same
                        AI workbench, dispute letters, e-signatures, and Stripe-paid settlements.
                    </p>
                    <Link to="/register">
                        <Button className="mt-6 bg-[#0F1E3D] hover:bg-[#1A2C57] h-11 px-6" data-testid="coverage-cta">
                            Start your free account
                        </Button>
                    </Link>
                </section>
            </main>

            <footer className="border-t border-slate-200 py-8 text-center text-xs text-slate-500">
                LienLift Direct · Judgment information &amp; document service · Not a law firm
            </footer>
        </div>
    );
}
